import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Building2, Users, FileText, DollarSign, TrendingUp, Percent, Tag, Plus, Pencil,
  Wrench, PaintRoller, Home as HomeIcon, Grid3x3, Boxes, Sparkles, Scissors,
  Brush, Hammer, Trees, Zap, Wind, Droplets, Truck, Wifi, Bug, Car, Camera, Download,
} from "lucide-react";

const ICON_OPTIONS = {
  Tag, Wrench, PaintRoller, Home: HomeIcon, Grid3x3, Boxes, Sparkles, Scissors,
  Brush, Hammer, Trees, Zap, Wind, Droplets, Truck, Wifi, Bug, Car, Camera,
};
const COLOR_OPTIONS = [
  { name: "Naranja", value: "#F97316" }, { name: "Esmeralda", value: "#10B981" },
  { name: "Azul", value: "#3B82F6" }, { name: "Violeta", value: "#8B5CF6" },
  { name: "Rosa", value: "#EC4899" }, { name: "Rojo", value: "#EF4444" },
  { name: "Ámbar", value: "#F59E0B" }, { name: "Cian", value: "#06B6D4" },
  { name: "Pizarra", value: "#475569" },
];

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-sm border border-slate-200 bg-white p-6" data-testid={`admin-stat-${label}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">{label}</span>
        <Icon className={`h-5 w-5 ${accent || "text-primary"}`} />
      </div>
      <p className="mt-3 font-head text-3xl font-extrabold text-secondary">{value}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const { t } = useLang();
  const [stats, setStats] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [suggested, setSuggested] = useState([]);
  const [custom, setCustom] = useState([]);
  const [dialog, setDialog] = useState(null); // { mode: 'promote'|'edit', label, key }
  const [icon, setIcon] = useState("Tag");
  const [color, setColor] = useState("#F97316");
  const [saving, setSaving] = useState(false);
  const [newLeads, setNewLeads] = useState(0);
  const [report, setReport] = useState(null);
  const [fromM, setFromM] = useState("");
  const [toM, setToM] = useState("");

  const visibleMonths = () => (report ? report.by_month.filter((m) => (!fromM || m.month >= fromM) && (!toM || m.month <= toM)) : []);

  const downloadCsv = () => {
    if (!report) return;
    const months = visibleMonths();
    const rows = [["Mes", "Facturas", "Facturado (USD)", "Comision (USD)"]];
    months.forEach((m) => rows.push([m.month, m.count, m.invoiced.toFixed(2), m.commission.toFixed(2)]));
    const totalRange = months.reduce((s, m) => s + m.commission, 0);
    rows.push([]);
    rows.push(["TOTAL RANGO", "", "", totalRange.toFixed(2)]);
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "")}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url; a.download = `comisiones-urbis-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const loadBiz = () => api.get("/admin/businesses").then((r) => setBusinesses(r.data)).catch(() => {});
  const loadSuggested = () => api.get("/categories/suggested").then((r) => setSuggested(r.data)).catch(() => {});
  const loadCustom = () => api.get("/categories").then((r) => setCustom(r.data.filter((c) => c.custom))).catch(() => {});
  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/leads").then((r) => setNewLeads((r.data || []).filter((l) => l.status !== "done").length)).catch(() => {});
    api.get("/admin/commission-report").then((r) => setReport(r.data)).catch(() => {});
    loadBiz(); loadSuggested(); loadCustom();
  }, []);

  const openPromote = (label) => { setDialog({ mode: "promote", label }); setIcon("Tag"); setColor("#F97316"); };
  const openEdit = (c) => { setDialog({ mode: "edit", label: c.es, key: c.key }); setIcon(c.icon || "Tag"); setColor(c.color || "#F97316"); };

  const save = async () => {
    setSaving(true);
    try {
      if (dialog.mode === "promote") {
        const { data } = await api.post("/admin/categories/promote", { label: dialog.label, icon, color });
        toast.success(`"${dialog.label}" ahora es oficial (${data.migrated} negocio(s) migrado(s))`);
      } else {
        await api.put(`/admin/categories/${dialog.key}`, { label: dialog.label, icon, color });
        toast.success(`"${dialog.label}" actualizada`);
      }
      setDialog(null); loadSuggested(); loadBiz(); loadCustom();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo guardar");
    } finally { setSaving(false); }
  };

  const togglePublish = async (b) => {
    await api.put(`/admin/businesses/${b.id}/publish`, null, { params: { published: !b.published } });
    toast.success(b.published ? "Despublicado" : "Publicado"); loadBiz();
  };

  const PreviewIcon = ICON_OPTIONS[icon] || Tag;

  return (
    <div className="mx-auto max-w-7xl px-6 py-10">
      <h1 className="font-head text-3xl font-extrabold tracking-tight text-secondary">{t("admin")}</h1>
      <a href="/admin/settings" className="mt-2 inline-flex items-center gap-2 rounded-sm bg-secondary px-4 py-2 text-sm font-semibold text-white hover:bg-slate-700" data-testid="admin-settings-link">Configuración comercial (precios, descuentos, prueba)</a>
      <a href="/admin/notifications" className="mt-2 ml-2 inline-flex items-center gap-2 rounded-sm bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-orange-600" data-testid="admin-notifications-link">Enviar notificación a negocios</a>
      <a href="/dashboard/leads" className="relative mt-2 ml-2 inline-flex items-center gap-2 rounded-sm bg-accent px-4 py-2 text-sm font-semibold text-white hover:opacity-90" data-testid="admin-master-inbox-link">
        Bandeja del negocio maestro (Solicitudes / Mensajes)
        {newLeads > 0 && <span className="ml-1 inline-flex min-w-[1.4rem] items-center justify-center rounded-full bg-white px-1.5 py-0.5 text-xs font-extrabold text-accent" data-testid="admin-new-leads-badge">{newLeads}</span>}
      </a>

      {stats && (
        <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-6">
          <Stat icon={Building2} label="Negocios" value={stats.total_businesses} />
          <Stat icon={Users} label="Usuarios" value={stats.total_users} />
          <Stat icon={FileText} label="Facturas" value={stats.total_invoices} />
          <Stat icon={DollarSign} label="Facturado" value={`$${stats.total_invoiced.toFixed(0)}`} accent="text-accent" />
          <Stat icon={Percent} label="Comisión plataforma" value={`$${stats.total_commission.toFixed(0)}`} accent="text-primary" />
          <Stat icon={TrendingUp} label="MRR" value={`$${stats.mrr.toFixed(0)}`} accent="text-accent" />
        </div>
      )}

      {report && (
        <div className="mt-10" data-testid="admin-commission-report">
          <h2 className="flex items-center gap-2 font-head text-xl font-bold text-secondary"><Percent className="h-5 w-5 text-primary" /> Reporte de comisiones cobradas</h2>
          <div className="mt-1 flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-slate-500">Comisiones de facturas pagadas. Total histórico: <b className="text-primary">${report.total_commission.toFixed(2)}</b></p>
            <button onClick={downloadCsv} className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 px-3.5 py-1.5 text-sm font-bold text-primary hover:bg-primary/5" data-testid="commission-csv-btn"><Download className="h-3.5 w-3.5" /> Descargar CSV</button>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="commission-date-filter">
            <span className="text-xs font-bold uppercase text-slate-400">Rango:</span>
            <select value={fromM} onChange={(e) => setFromM(e.target.value)} className="h-9 rounded-sm border border-input px-2 text-sm" data-testid="commission-from">
              <option value="">Desde (inicio)</option>
              {report.by_month.map((m) => <option key={m.month} value={m.month}>{m.month}</option>)}
            </select>
            <span className="text-slate-400">→</span>
            <select value={toM} onChange={(e) => setToM(e.target.value)} className="h-9 rounded-sm border border-input px-2 text-sm" data-testid="commission-to">
              <option value="">Hasta (fin)</option>
              {report.by_month.map((m) => <option key={m.month} value={m.month}>{m.month}</option>)}
            </select>
            {(fromM || toM) && <button onClick={() => { setFromM(""); setToM(""); }} className="text-xs font-semibold text-slate-400 hover:text-primary" data-testid="commission-clear-filter">Limpiar</button>}
          </div>

          <div className="mt-4 flex flex-wrap gap-3" data-testid="commission-by-plan">
            {["basic", "pro", "enterprise", "sin_plan"].filter((k) => report.by_plan[k]).map((k) => (
              <div key={k} className="rounded-sm border border-slate-200 bg-white px-4 py-3" data-testid={`commission-plan-${k}`}>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400">{k === "sin_plan" ? "Sin plan" : k === "basic" ? "Básico" : k === "enterprise" ? "Empresarial" : "Pro"}</p>
                <p className="font-head text-xl font-extrabold text-primary">${report.by_plan[k].commission.toFixed(2)}</p>
                <p className="text-xs text-slate-500">{report.by_plan[k].count} facturas</p>
              </div>
            ))}
          </div>

          <div className="mt-4 overflow-hidden rounded-sm border border-slate-200 bg-white">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs font-bold uppercase tracking-wider text-slate-400">
                <tr><th className="p-3">Mes</th><th className="p-3">Facturas</th><th className="p-3">Facturado</th><th className="p-3">Comisión</th></tr>
              </thead>
              <tbody data-testid="commission-by-month">
                {visibleMonths().map((m) => (
                  <tr key={m.month} className="border-t border-slate-100" data-testid={`commission-month-${m.month}`}>
                    <td className="p-3 font-semibold text-secondary">{m.month}</td>
                    <td className="p-3 text-slate-500">{m.count}</td>
                    <td className="p-3 text-slate-500">${m.invoiced.toFixed(2)}</td>
                    <td className="p-3 font-bold text-primary">${m.commission.toFixed(2)}</td>
                  </tr>
                ))}
                {visibleMonths().length === 0 && <tr><td colSpan={4} className="p-6 text-center text-slate-400">Sin comisiones en este rango.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {suggested.length > 0 && (
        <div className="mt-10" data-testid="admin-suggested-categories">
          <h2 className="flex items-center gap-2 font-head text-xl font-bold text-secondary"><Tag className="h-5 w-5 text-primary" /> Categorías sugeridas</h2>
          <p className="mt-1 text-sm text-slate-500">Convierte las categorías personalizadas populares en categorías oficiales del directorio.</p>
          <div className="mt-4 flex flex-wrap gap-3">
            {suggested.map((s) => (
              <div key={s.label} className="flex items-center gap-3 rounded-sm border border-slate-200 bg-white px-4 py-2.5" data-testid={`admin-suggested-${s.label}`}>
                <span className="text-sm font-semibold text-secondary">{s.label}</span>
                <span className="rounded-sm bg-slate-100 px-1.5 py-0.5 text-xs font-bold text-slate-500">{s.count}</span>
                <Button size="sm" className="rounded-sm bg-primary hover:bg-orange-600" onClick={() => openPromote(s.label)} data-testid={`admin-promote-${s.label}`}><Plus className="mr-1 h-3.5 w-3.5" />Oficializar</Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {custom.length > 0 && (
        <div className="mt-10" data-testid="admin-custom-categories">
          <h2 className="flex items-center gap-2 font-head text-xl font-bold text-secondary"><Tag className="h-5 w-5 text-accent" /> Categorías oficiales personalizadas</h2>
          <p className="mt-1 text-sm text-slate-500">Edita el icono y color de las categorías que ya oficializaste.</p>
          <div className="mt-4 flex flex-wrap gap-3">
            {custom.map((c) => {
              const CIcon = ICON_OPTIONS[c.icon] || Tag;
              return (
                <div key={c.key} className="flex items-center gap-3 rounded-sm border border-slate-200 bg-white px-4 py-2.5" data-testid={`admin-custom-${c.key}`}>
                  <span className="flex h-8 w-8 items-center justify-center rounded-sm text-white" style={{ backgroundColor: c.color || "#475569" }}><CIcon className="h-4 w-4" /></span>
                  <span className="text-sm font-semibold text-secondary">{c.es}</span>
                  <Button size="sm" variant="outline" className="rounded-sm" onClick={() => openEdit(c)} data-testid={`admin-edit-${c.key}`}><Pencil className="mr-1 h-3.5 w-3.5" />Editar</Button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <h2 className="mt-10 font-head text-xl font-bold text-secondary">Negocios registrados</h2>
      <div className="mt-4 overflow-x-auto rounded-sm border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="admin-businesses-table">
          <thead className="border-b border-slate-200 bg-slate-50">
            <tr className="text-left text-xs font-bold uppercase tracking-wider text-slate-400">
              <th className="p-3">Negocio</th><th className="p-3">Ciudad</th><th className="p-3">Plan</th><th className="p-3">Estado</th><th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {businesses.map((b) => (
              <tr key={b.id} className="border-b border-slate-100" data-testid={`admin-biz-row-${b.id}`}>
                <td className="p-3 font-semibold text-secondary">{b.name}</td>
                <td className="p-3 text-slate-600">{b.city}, {b.state}</td>
                <td className="p-3 text-slate-500">{b.subscription?.plan || "—"}</td>
                <td className="p-3"><span className={`rounded-sm px-2 py-0.5 text-xs font-bold uppercase ${b.published ? "bg-accent/10 text-accent" : "bg-slate-100 text-slate-500"}`}>{b.published ? "Publicado" : "Oculto"}</span></td>
                <td className="p-3"><Button size="sm" variant="outline" onClick={() => togglePublish(b)} className="rounded-sm" data-testid={`admin-toggle-${b.id}`}>{b.published ? "Ocultar" : "Publicar"}</Button></td>
              </tr>
            ))}
            {businesses.length === 0 && <tr><td colSpan={5} className="p-10 text-center text-slate-400">—</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="sm:max-w-md" data-testid="promote-dialog">
          <DialogHeader>
            <DialogTitle>{dialog?.mode === "edit" ? "Editar" : "Oficializar"} "{dialog?.label}"</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-2">
            <div className="flex items-center gap-3">
              <span className="flex h-14 w-14 items-center justify-center rounded-sm text-white" style={{ backgroundColor: color }} data-testid="promote-preview">
                <PreviewIcon className="h-7 w-7" />
              </span>
              <div>
                <p className="text-sm font-semibold text-secondary">{dialog?.label}</p>
                <p className="text-xs text-slate-500">Así se verá en el directorio</p>
              </div>
            </div>
            <div>
              <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">Icono</p>
              <div className="grid grid-cols-8 gap-2">
                {Object.entries(ICON_OPTIONS).map(([name, I]) => (
                  <button key={name} type="button" onClick={() => setIcon(name)}
                    className={`flex h-9 w-9 items-center justify-center rounded-sm border transition-colors ${icon === name ? "border-primary bg-primary/10 text-primary" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}
                    data-testid={`promote-icon-${name}`}><I className="h-4 w-4" /></button>
                ))}
              </div>
            </div>
            <div>
              <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">Color</p>
              <div className="flex flex-wrap gap-2">
                {COLOR_OPTIONS.map((c) => (
                  <button key={c.value} type="button" title={c.name} onClick={() => setColor(c.value)}
                    className={`h-8 w-8 rounded-full border-2 transition-transform hover:scale-110 ${color === c.value ? "border-secondary" : "border-transparent"}`}
                    style={{ backgroundColor: c.value }} data-testid={`promote-color-${c.value}`} />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setDialog(null)} data-testid="promote-cancel">Cancelar</Button>
            <Button className="rounded-sm bg-primary hover:bg-orange-600" disabled={saving} onClick={save} data-testid="promote-confirm">{saving ? "Guardando…" : (dialog?.mode === "edit" ? "Guardar" : "Oficializar")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
